import { useId } from 'react';
import { useTranslate } from 'ra-core';
import { AutocompleteArrayInput } from '../../components/admin/autocomplete-array-input';
import { ReferenceArrayInput } from '../../components/admin/reference-array-input';
import {
  SearchInput,
  type SearchInputProps,
} from '../../components/admin/search-input';
import { SelectInput } from '../../components/admin/select-input';
import { Label } from '../../components/ui/label';
import { filterAutocompleteClassName } from '../Moonstar/filterClasses';

interface BasicUserFiltersConfig {
  unitFilterDisabled?: boolean;
  isSmallScreen: boolean;
}

type UserFilterReference = 'departments' | 'jobTitles' | 'regions' | 'units';

const UserSearchInput = (props: SearchInputProps) => {
  const translate = useTranslate();

  const id = useId();

  return (
    <div className="grid w-full min-w-0 gap-2">
      <Label
        htmlFor={id}
        className="text-xs leading-none text-muted-foreground"
      >
        {translate('moonstar.user.filter.labels.users', {
          _: 'Search users',
        })}
      </Label>
      <SearchInput
        {...props}
        id={id}
        placeholder={translate('moonstar.user.filter.placeholders.users', {
          _: 'Search by name, email, phone number, or employee ID…',
        })}
      />
    </div>
  );
};

const UserReferenceFilterInput = ({
  label,
  reference,
  disabled,
}: {
  label: string;
  reference: UserFilterReference;
  disabled: boolean;
}) => {
  const translate = useTranslate();
  return (
    <AutocompleteArrayInput
      label={label}
      optionText="name"
      placeholder={translate(`moonstar.user.filter.placeholders.${reference}`)}
      className={filterAutocompleteClassName}
      filterToQuery={(q: string) => ({ name: q })}
      disabled={disabled}
    />
  );
};

const referenceFilter = (
  label: string,
  source: string,
  reference: UserFilterReference,
  alwaysOn: boolean,
  disabled = false
) => (
  <ReferenceArrayInput
    key={source}
    label={label}
    source={source}
    reference={reference}
    perPage={999}
    sort={{ field: 'name', order: 'ASC' }}
    alwaysOn={alwaysOn}
  >
    <UserReferenceFilterInput
      label={label}
      reference={reference}
      disabled={disabled}
    />
  </ReferenceArrayInput>
);

/** The user filters shared by the user lists (search + department, job title, region and units). */
export const createBasicUserFilters = ({
  unitFilterDisabled = false,
  isSmallScreen,
}: BasicUserFiltersConfig) => [
  <UserSearchInput key="q" source="q" alwaysOn />,
  referenceFilter('Department', 'departmentId', 'departments', !isSmallScreen),
  referenceFilter('Job Title', 'jobTitleId', 'jobTitles', !isSmallScreen),
  referenceFilter('Region', 'regionId', 'regions', !isSmallScreen),
  referenceFilter(
    'Units',
    'unitIds',
    'units',
    !isSmallScreen,
    unitFilterDisabled
  ),
];

const ALL_USERS = 'all';

// The kit `SelectInput` has no empty item, so "All" is an explicit choice (react-admin's `NullableBooleanInput` had one)
const UserDisabledFilter = (
  <SelectInput
    label="moonstar.user.filter.userIsDisabled.label"
    source="userIsDisabled"
    className="w-fit"
    choices={[
      {
        id: ALL_USERS,
        name: 'moonstar.user.filter.userIsDisabled.nullLabel',
      },
      {
        id: 'true',
        name: 'moonstar.user.filter.userIsDisabled.trueLabel',
      },
      {
        id: 'false',
        name: 'moonstar.user.filter.userIsDisabled.falseLabel',
      },
    ]}
    emptyText="moonstar.user.filter.userIsDisabled.nullLabel"
    emptyValue={ALL_USERS}
    // `null` rather than `undefined`: react-hook-form shows the default value again for `undefined`. The empty filter
    // value is then removed from the list filters.
    parse={(value: string) =>
      value === ALL_USERS || value === '' ? null : value === 'true'
    }
    format={(value: boolean | null | undefined) =>
      value == null ? ALL_USERS : String(value)
    }
    alwaysOn
  />
);

export const createUserFilters = ({
  isSmallScreen,
  isAdmin = false,
}: {
  isSmallScreen: boolean;
  isAdmin?: boolean;
}) => [
  ...createBasicUserFilters({
    isSmallScreen,
    unitFilterDisabled: !isAdmin,
  }),
  UserDisabledFilter,
];
